<script>
import axios from "@/lib/axios";
import Swal from "sweetalert2";

export default {
  props: {
    chat: {
      type: Object,
      required: true,
    },
    kategories: {
      type: Array,
      default: () => []
    },
    currentUser: {
      type: Object,
      default: () => ({})
    },
    userRoles: {
      type: Array,
      default: () => []
    }
  },

  data() {
    return {
      replyText: '',
      isSubmitting: false,
      isClosing: false,
      localChatDetails: []
    };
  },

  computed: {
    isCurrentUserAdmin() {
      return this.userRoles.some(role => 
        role.toLowerCase().includes('admin') || 
        role.toLowerCase().includes('administrator')
      );
    },

    canCloseChat() {
      if (!this.currentUser?.id) return false;
      return this.isCurrentUserAdmin || this.chat.id_user_pengirim === this.currentUser.id;
    },
  },

  watch: {
    chat: {
      immediate: true,
      handler(newChat) {
        if (newChat && newChat.details) {
          this.localChatDetails = [...newChat.details];
        }
      }
    }
  },

  methods: {
    isUserAdmin(user) {
      if (!user) return false;
      
      if (user.user_roles && Array.isArray(user.user_roles)) {
        return user.user_roles.some(role => 
          role.toLowerCase().includes('admin') || 
          role.toLowerCase().includes('administrator')
        );
      }
      
      if (user.user_id === this.currentUser?.id) {
        return this.isCurrentUserAdmin;
      }
      
      return false;
    },

    getKategoriName(kategoriId) {
      const kategori = this.kategories.find(k => k.id === kategoriId);
      return kategori ? kategori.nama : 'Unknown';
    },

    getKategoriBadgeClass(kategoriId) {
      const kategori = this.kategories.find(k => k.id === kategoriId);
      return kategori ? kategori.color : 'bg-secondary';
    },

    getUserAvatarClass(user) {
      if (this.isUserAdmin(user)) return 'bg-success text-white';
      if (user.user_id === this.chat.id_user_pengirim) return 'bg-primary text-white';
      return 'bg-light text-dark';
    },

    async addReply() {
      if (!this.replyText.trim()) return;
      
      this.isSubmitting = true;
      try {
        const response = await axios.post(`/api/chats/${this.chat.id}/reply`, {
          chat: this.replyText
        });
        
        this.localChatDetails.push({
          ...response.data.chatDetail,
          user_nama: this.currentUser.nama,
          user_id: this.currentUser.id
        });
        
        this.replyText = '';
        
        this.$nextTick(() => {
          this.scrollToBottom();
        });
        
        this.$emit('reply-added', {
          ...this.chat,
          details: this.localChatDetails,
          status: this.isCurrentUserAdmin && this.chat.status === 'open' ? 'answered' : this.chat.status
        });
        
      } catch (error) {
        console.log('Gagal mengirim balasan: ', error);
        Swal.fire({
          icon: "error",
          title: "Gagal",
          text: "Gagal mengirim balasan!",
        });
      } finally {
        this.isSubmitting = false;
      }
    },

    async closeChat() {
      this.isClosing = true;
      try {
        await axios.put(`/api/chats/${this.chat.id}/status`, {
          status: 'closed'
        });
        
        // Update status lokal
        const updatedChat = {
          ...this.chat,
          status: 'closed'
        };
        
        this.$emit('status-updated', updatedChat);
        
        Swal.fire({
          icon: "success",
          title: "Berhasil",
          text: "Diskusi berhasil ditutup!",
          timer: 1500,
          showConfirmButton: false
        });
      } catch (error) {
        console.log('Gagal menutup diskusi');
        Swal.fire({
          icon: "error",
          title: "Gagal",
          text: "Gagal menutup diskusi!",
        });
      } finally {
        this.isClosing = false;
      }
    },

    getUserInitial(name) {
      return name ? name.charAt(0).toUpperCase() : 'U';
    },

    formatDateTime(date) {
      return new Date(date).toLocaleString('id-ID');
    },

    getStatusBadgeClass(status) {
      const classes = {
        open: 'bg-warning',
        answered: 'bg-success',
        closed: 'bg-secondary'
      };
      return classes[status] || 'bg-secondary';
    },

    getStatusText(status) {
      const texts = {
        open: 'Open',
        answered: 'Answered',
        closed: 'Closed'
      };
      return texts[status] || status;
    },

    scrollToBottom() {
      this.$nextTick(() => {
        const container = this.$refs.chatMessages;
        if (container) {
          container.scrollTop = container.scrollHeight;
        }
      });
    }
  },

  mounted() {
    this.scrollToBottom();
  }
};
</script>

<template>
  <div class="modal fade show" style="display: block">
    <div class="modal-dialog modal-lg">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">
            <span :class="getKategoriBadgeClass(chat.id_kategori_komplain)" class="badge me-2">
              {{ getKategoriName(chat.id_kategori_komplain) }}
            </span>
            <span :class="getStatusBadgeClass(chat.status)" class="badge">
              {{ getStatusText(chat.status) }}
            </span>
          </h5>
          <button type="button" class="btn-close" @click="$emit('close')"></button>
        </div>
        <div class="modal-body">
          <!-- Chat Messages -->
          <div class="chat-messages" style="max-height: 400px; overflow-y: auto;" ref="chatMessages">
            <div v-for="detail in localChatDetails" :key="detail.id" 
                 class="chat-message mb-4">
              <div class="d-flex">
                <div class="flex-shrink-0">
                  <div class="avatar-xs">
                    <div 
                      :class="getUserAvatarClass(detail)" 
                      class="avatar-title rounded-circle fw-bold"
                    >
                      {{ getUserInitial(detail.user_nama) }}
                    </div>
                  </div>
                </div>
                <div class="flex-grow-1 ms-3">
                  <h6 class="mb-1">
                    {{ detail.user_nama }}
                    <small class="text-muted ms-2">{{ formatDateTime(detail.created_at) }}</small>
                    <span v-if="detail.user_id === chat.id_user_pengirim" class="badge bg-primary ms-1">
                      Pengirim
                    </span>
                    <span v-if="isUserAdmin(detail)" class="badge bg-success ms-1">
                      Admin
                    </span>
                  </h6>
                  <div class="chat-content bg-light p-3 rounded">
                    {{ detail.chat }}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Reply Form -->
          <div v-if="chat.status !== 'closed'" class="reply-section mt-4">
            <form @submit.prevent="addReply">
              <div class="mb-3">
                <label class="form-label">Balasan</label>
                <textarea v-model="replyText" class="form-control" rows="3" 
                          placeholder="Tulis balasan Anda..." required></textarea>
              </div>
              <div class="d-flex justify-content-between align-items-center">
                <div>
                  <button 
                    v-if="canCloseChat"
                    :disabled="isClosing"
                    type="button" 
                    @click="closeChat"
                    class="btn btn-outline-secondary"
                  >
                    <span v-if="isClosing" class="spinner-border spinner-border-sm me-1"></span>
                    <i class="ri-lock-line me-1"></i>
                    Tutup Diskusi
                  </button>
                </div>
                <div>
                  <button type="submit" class="btn btn-primary" :disabled="isSubmitting">
                    <span v-if="isSubmitting" class="spinner-border spinner-border-sm me-1"></span>
                    <i class="ri-send-plane-line me-1"></i>
                    Kirim Balasan
                  </button>
                </div>
              </div>
            </form>
          </div>

          <!-- Closed Notice -->
          <div v-else class="alert alert-info mt-4">
            <i class="ri-information-line me-2"></i>
            Diskusi ini telah ditutup dan tidak dapat menerima balasan baru.
          </div>
        </div>
      </div>
    </div>
  </div>
  <div class="modal-backdrop fade show"></div>
</template>

<style scoped>
.chat-content {
  white-space: pre-wrap;
  word-wrap: break-word;
}
.avatar-xs {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
}
</style>